/**
 * Colour maths shared by the theme layer and the native system bars.
 *
 * Kept in one place because two very different consumers must reach the SAME
 * verdict about a background: the branding store, which pairs every surface
 * with a readable text token, and native/systemBars.js, which pairs the status
 * bar background with readable system icons. Two implementations would
 * eventually disagree, and the disagreement looks like invisible content.
 */

/** '#rrggbb' -> [r, g, b], or null when the value is not a 6-digit hex. */
export function parseHex(hex) {
  if (!hex) return null
  const m = String(hex).trim().replace('#', '')
  if (m.length !== 6) return null
  const r = parseInt(m.slice(0, 2), 16)
  const g = parseInt(m.slice(2, 4), 16)
  const b = parseInt(m.slice(4, 6), 16)
  if ([r, g, b].some((v) => Number.isNaN(v))) return null
  return [r, g, b]
}

/** WCAG relative luminance (0 = black, 1 = white). */
export function relLuminance([r, g, b]) {
  const lin = [r, g, b].map((v) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2]
}

/** WCAG contrast ratio between two 6-digit hex colours. */
export function contrastRatio(hexA, hexB) {
  const a = parseHex(hexA)
  const b = parseHex(hexB)
  if (!a || !b) return 1
  const la = relLuminance(a) + 0.05
  const lb = relLuminance(b) + 0.05
  return la > lb ? la / lb : lb / la
}

/**
 * Keep a preferred foreground when it meets the requested contrast, otherwise
 * choose the more readable shared light/dark text token.
 */
export function readableOn(bgHex, preferredHex, min = 4.5) {
  const textDark = '#1f2937'
  const textLight = '#f1f5f9'
  if (preferredHex && contrastRatio(bgHex, preferredHex) >= min) return preferredHex
  if (!parseHex(bgHex)) return preferredHex || textDark
  return contrastRatio(bgHex, textDark) >= contrastRatio(bgHex, textLight)
    ? textDark
    : textLight
}

/**
 * Add readable foreground vars for parseable hex backgrounds without replacing
 * explicit foreground overrides already present in the map.
 */
export function deriveReadableInkVars(vars, pairs) {
  for (const [backgroundVar, inkVar] of pairs) {
    if (!vars[inkVar] && parseHex(vars[backgroundVar])) {
      vars[inkVar] = readableOn(vars[backgroundVar])
    }
  }
  return vars
}

/** Darken a six-digit hex colour by mixing it toward black. */
export function darkenHex(hex, amount = 0.1) {
  const rgb = parseHex(hex)
  if (!rgb) return null
  const ratio = Math.max(0, Math.min(1, Number(amount)))
  return `#${rgb
    .map((channel) => Math.round(channel * (1 - ratio)).toString(16).padStart(2, '0'))
    .join('')}`
}

/** Lighten a six-digit hex colour by mixing it toward white. */
export function lightenHex(hex, amount = 0.1) {
  const rgb = parseHex(hex)
  if (!rgb) return null
  const ratio = Math.max(0, Math.min(1, Number(amount)))
  return `#${rgb
    .map((channel) => Math.round(channel + (255 - channel) * ratio).toString(16).padStart(2, '0'))
    .join('')}`
}

/**
 * Whether `hex` is dark enough to need light foreground content on top of it.
 * Unparseable input counts as light, which is what an absent or invalid brand
 * colour falls back to anyway.
 */
export function isDarkSurface(hex) {
  const rgb = parseHex(hex)
  if (!rgb) return false
  return relLuminance(rgb) < 0.5
}

const THEME_COLOR_TOKEN_NAMES = new Set([
  'primary',
  'accent',
  'success',
  'warning',
  'error',
  'info',
  'secondary',
])

function themeColorToken(color) {
  if (!color) return null
  const token = String(color).trim().toLowerCase()
  return THEME_COLOR_TOKEN_NAMES.has(token) ? token : null
}

/**
 * Turn a stored theme token into a live CSS colour while preserving literal
 * hex/RGB values. The CSS variable remains reactive when branding or an
 * interface preset changes the active Vuetify palette at runtime.
 */
export function resolveThemeTokenColor(color, fallback = null) {
  if (!color) return fallback
  const value = String(color).trim()
  if (value.startsWith('#') || value.startsWith('rgb')) return value
  const token = themeColorToken(value)
  return token
    ? `rgb(var(--v-theme-${token}))`
    : fallback
}

/**
 * Resolve a background together with readable ink for content painted on it.
 * Theme tokens use their live on-* companion; literal hex colours use the
 * shared contrast helper. Other CSS colours preserve the caller's ink.
 */
export function resolveThemeTokenColorPair(
  color,
  fallback = null,
  fallbackInk = '#ffffff',
) {
  const token = themeColorToken(color)
  const background = resolveThemeTokenColor(color, fallback)
  let ink = fallbackInk
  if (token) ink = `rgb(var(--v-theme-on-${token}))`
  else if (parseHex(background)) ink = readableOn(background)
  return { background, ink }
}
